import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Group, Stack, Text, Badge } from '@repletoryreach/sdk-ui';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../../lib/format';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { Blank, Money, OrgCell } from '../../components/cells';
import { RowMenu } from '../../../../components/RowMenu';
import type { Indexed } from '../../../../lib/types';
import { useT, type Translate } from '../../../../i18n/I18nProvider';

export function statusTone(s: string): 'healthy' | 'warning' | 'inactive' | 'pending' | 'down' {
  switch (s) {
    case 'active': return 'healthy';
    case 'past_due': return 'warning';
    case 'cancelled': return 'down';
    default: return 'inactive';
  }
}

/**
 * The wire carries `past_due`; a person reads "Past due". The raw code is the
 * fallback so a status the catalogue has not caught up with still renders.
 */
export function statusLabel(t: Translate, s: string): string {
  return t(`billing.subscriptions.status.${s}`, s);
}

/** The cycle a subscription is in, as a period rather than only its end date. */
export function cycleLabel(s: admin.AdminSubscription): string {
  return `${formatDate(s.billing_cycle_start)} → ${formatDate(s.billing_cycle_end)}`;
}

/**
 * What the row needs the operator to notice about how the plan is paid (11 October 2026): the platform's own
 * business, a paid plan with no card to charge on the 1st, and a cheaper plan booked for the next renewal. Extra use
 * on and active (30 October 2026) is the "+" of Growth+ and Scale+.
 */
export function PlanFlags({ s, paid, planLabel }: { s: admin.AdminSubscription; paid: boolean; planLabel: (planId: string) => string }) {
  const t = useT();
  const live = s.status !== 'cancelled' && !s.cancelled_at;
  const flags = [
    s.house_account && <Badge key="house" size="xs" variant="light" color="violet">{t('billing.subscriptions.flag.house')}</Badge>,
    s.pays_by_invoice && <Badge key="invoice" size="xs" variant="light" color="teal">{t('billing.subscriptions.flag.invoice')}</Badge>,
    live && s.extra_use && <Badge key="extra" size="xs" variant="light" color="blue" data-testid="flag-extra-use">{t('billing.subscriptions.flag.extraUse')}</Badge>,
    live && paid && s.has_card === false && !s.pays_by_invoice && <Badge key="card" size="xs" variant="light" color="orange">{t('billing.subscriptions.flag.noCard')}</Badge>,
    live && s.pending_plan_id && (
      <Badge key="pending" size="xs" variant="light" color="blue">
        {t('billing.subscriptions.flag.pending', { plan: planLabel(s.pending_plan_id), date: formatDate(s.billing_cycle_end) })}
      </Badge>
    ),
    live && s.ends_at && <Badge key="ends" size="xs" variant="light" color="gray">{t('billing.subscriptions.flag.ends', { date: formatDate(s.ends_at) })}</Badge>
  ].filter(Boolean);
  return flags.length > 0 ? <Group gap={4} wrap="wrap">{flags}</Group> : null;
}

interface Props {
  records: admin.AdminSubscription[];
  loading: boolean;
  planLabel: (planId: string) => string;
  /** The plan behind the row, for the per-row revenue column. */
  planPrice: (planId: string) => { amount_minor: number; currency: string } | undefined;
  onRowClick: (s: admin.AdminSubscription) => void;
  onOpenLedger: (s: admin.AdminSubscription) => void;
  onOpenInvoices: (s: admin.AdminSubscription) => void;
}

export function SubscriptionsTable({ records, loading, planLabel, planPrice, onRowClick, onOpenLedger, onOpenInvoices }: Props) {
  const t = useT();
  return (
    <DataTable<Indexed<admin.AdminSubscription>>
      records={records as Indexed<admin.AdminSubscription>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('billing.subscriptions.empty')}
      onRowClick={(r) => onRowClick(r)}
      columns={[
        {
          accessor: 'organisation_id',
          title: t('billing.subscriptions.columns.organisation'),
          render: (r) => <OrgCell id={r.organisation_id} />
        },
        {
          accessor: 'plan_id',
          title: t('billing.subscriptions.columns.plan'),
          // The flags are badges (blocks), so they sit under the code rather than in a line of text.
          render: (r) => (
            <Stack gap={4}>
              <code>{planLabel(r.plan_id)}</code>
              <PlanFlags s={r} paid={(planPrice(r.plan_id)?.amount_minor ?? 0) > 0} planLabel={planLabel} />
            </Stack>
          )
        },
        {
          accessor: 'status',
          title: t('common.status'),
          sortable: true,
          render: (r) => <StatusBadge status={statusTone(r.status)}>{statusLabel(t, r.status)}</StatusBadge>
        },
        {
          accessor: 'billing_cycle_end',
          title: t('billing.subscriptions.columns.cycle'),
          sortable: true,
          // A past-due row's cycle ended in the past, which is the de-facto
          // "past due since": it earns the error token, every other row does not.
          render: (r) => (
            <span title={cycleLabel(r)}>
              <TwoLineCell
                primary={cycleLabel(r)}
                secondary={r.status === 'past_due'
                  ? <Text role="caption" c="var(--rr-color-error)">{t('billing.subscriptions.pastDueSince', { when: formatRelative(r.billing_cycle_end) })}</Text>
                  : undefined}
              />
            </span>
          )
        },
        {
          accessor: 'plan_price',
          title: t('billing.subscriptions.columns.mrr'),
          textAlign: 'right',
          // The revenue a live subscription carries is its plan's monthly
          // price. A cancelled row carries none, and says so rather than
          // showing a figure the platform is no longer billing.
          render: (r) => {
            const price = planPrice(r.plan_id);
            if (!price || r.status === 'cancelled' || r.cancelled_at) return <Blank />;
            return <Money minor={price.amount_minor} currency={price.currency} />;
          }
        },
        {
          accessor: 'billing_cycle_start',
          title: t('billing.subscriptions.columns.nextRenewal'),
          sortable: true,
          render: (r) => (r.status === 'cancelled' || r.cancelled_at)
            ? <Blank />
            : <span title={r.billing_cycle_end}><TwoLineCell primary={formatDate(r.billing_cycle_end)} secondary={formatRelative(r.billing_cycle_end)} /></span>
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (r) => (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <RowMenu
                actions={[
                  { id: 'org', label: t('billing.subscriptions.action.viewOrg'), onSelect: () => onRowClick(r) },
                  { id: 'ledger', label: t('billing.subscriptions.action.viewLedger'), onSelect: () => onOpenLedger(r) },
                  { id: 'invoices', label: t('billing.subscriptions.action.viewInvoices'), onSelect: () => onOpenInvoices(r) }
                ]}
              />
            </Group>
          )
        }
      ]}
    />
  );
}
